import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as amplitude from '@amplitude/analytics-browser';

import {
  Button,
  FormatCurrencyForDisplay,
} from '@unified-checkout/design-system';
import { ArrowRight, Bolt } from '@unified-checkout/design-system/icons/index';

import type { TBalanceModals } from '@app/dashboard/types/innerLayout/balance_and_transfers/balance/modals';
import type {
  IBalanceItem,
  IBalancesProps,
  TUpcomingTypeTYPES,
} from '@app/dashboard/types/innerLayout/balance_and_transfers/balance/balances';
import {
  BALANCE_TYPES,
  MINIMUM_TRANSFER_AMOUNT,
  UPCOMING_BALANCE_TYPES,
  UPCOMING_BALANCES_TYPE_CONFIG,
} from '@app/dashboard/constants/balance_and_transfers/balance';
import {
  BALANCE_TRANSFERS_EVENTS,
  BALANCE_TRANSFERS_EVENTS_PROPERTIES,
} from '@app/dashboard/constants/tracking_events/balance-and-transfers';
import { BALANCE_MODALS } from '@app/dashboard/constants/balance_and_transfers/balance/modals';
import { LOCAL_STORAGE_KEYS } from '@app/dashboard/constants/local-storage-keys';
import { useLangStore } from '@app/dashboard/store/lang';
import { formatAmountWithCurrency } from '@app/dashboard/views/pages/inner_layout/BalanceAndTransfers/utils';
import { getFromLocalStorage } from '@app/dashboard/utils/manageLocalStorage';
import TableInfo from '@app/dashboard/components/inner_layout/TableInfo';

import { handleStoreLastAction } from '../../../../utils/events-tracking';
import BalanceItem from './BalanceItem';
import InstantSettlement from './InstantSettlement';

import styles from './balances.module.scss';

const Balances = ({
  availableBalance,
  pendingBalance,
  onItsWayBalance,
  upcomingBalance,
  isLoading,
  currency,
}: IBalancesProps) => {
  const { t } = useTranslation();
  const { lang } = useLangStore();
  const [openModal, setOpenModal] = useState({
    settlement: false,
    withdraw: false,
  });

  const handleOpenModal = (modalType: TBalanceModals) => {
    setOpenModal((prevState) => ({
      ...prevState,
      [modalType]: true,
    }));
  };

  const handleCloseModal = (modalType: TBalanceModals) => {
    setOpenModal((prevState) => ({
      ...prevState,
      [modalType]: false,
    }));
  };

  const isInstantSettlementEnabled = useMemo(
    () => getFromLocalStorage(LOCAL_STORAGE_KEYS.INSTANT_SETTLEMENT_ENABLED),
    []
  );

  const shouldDisableInstantSettlement = useMemo(() => {
    const pendingAmount = pendingBalance?.amount ?? 0;

    return (
      pendingAmount < MINIMUM_TRANSFER_AMOUNT ||
      pendingAmount <= 0 ||
      !pendingBalance?.settle_now_enabled
    );
  }, [pendingBalance?.settle_now_enabled, pendingBalance?.amount]);

  const upcomingTypes = useMemo(() => UPCOMING_BALANCES_TYPE_CONFIG(t), [t]);

  const shouldShowInstantSettlementButton =
    isInstantSettlementEnabled && pendingBalance?.settle_now_enabled !== null;

  const columns = [
    {
      id: 'value_date',
      name: t('innerLayout.balanceTransfers.balance.balances.valueDate'),
      select: (row: { value_date?: string }) => row?.value_date,
      render: (value_date: string) => value_date,
    },
    {
      id: 'type',
      name: t('innerLayout.balanceTransfers.balance.balances.type'),
      select: (row: { type?: string }) => row?.type,
      render: (type: string) => {
        const upcomingType = type ?? UPCOMING_BALANCE_TYPES.OTHER;

        const upcomingTypeConfig =
          upcomingTypes[upcomingType as TUpcomingTypeTYPES];

        return (
          <div className={styles['balances__upcoming-type']}>
            <div
              className={styles['balances__upcoming-type-icon']}
              data-balance-upcoming-type={upcomingType}
            >
              {upcomingTypeConfig?.icon}
            </div>
            <div className={styles['balances__upcoming-type-label']}>
              {upcomingTypeConfig?.label}
            </div>
          </div>
        );
      },
    },
    {
      id: 'amount',
      name: t('innerLayout.balanceTransfers.balance.balances.amount'),
      select: (row: { amount?: number }) => row?.amount,
      render: (amount: string) => {
        const formattedAmount = formatAmountWithCurrency({
          amount: Number(amount),
          currency,
          lang,
        });
        const finalAmount = FormatCurrencyForDisplay(formattedAmount);
        return finalAmount as any;
      },
    },
  ];

  const handleSettlementClicked = () => {
    const eventName = BALANCE_TRANSFERS_EVENTS.BALANCE_PAGE.SETTLE_NOW_CLICKED;
    amplitude.track(eventName, {
      [BALANCE_TRANSFERS_EVENTS_PROPERTIES.SHARED.CURRENCY]: currency,
      [BALANCE_TRANSFERS_EVENTS_PROPERTIES.SHARED.PENDING_AMOUNT]:
        pendingBalance?.amount,
      [BALANCE_TRANSFERS_EVENTS_PROPERTIES.BALANCE_PAGE
        .UPCOMING_SETTLEMENT_AMOUNT]: upcomingBalance?.total_amount,
    });
    handleOpenModal(BALANCE_MODALS.SETTLEMENT);
    handleStoreLastAction(eventName);
  };

  const BALANCES: IBalanceItem[] = [
    {
      id: BALANCE_TYPES.PENDING as IBalanceItem['id'],
      label: t('innerLayout.balanceTransfers.balance.balances.pending'),
      value: pendingBalance?.amount,
      description: pendingBalance?.label,
      // description: (
      //   <>
      //     {FormatCurrencyForDisplay(
      //       formatAmountWithCurrency({
      //         lang,
      //         amount: upcomingBalance?.total_amount || 0,
      //         currency,
      //       }),
      //       false
      //     )}
      //     {t('innerLayout.balanceTransfers.balance.balances.pendingHint', {
      //       total: upcomingBalance?.total_dates,
      //     })}
      //   </>
      // ),
      renderAsAccordion: true,
      action: shouldShowInstantSettlementButton && (
        <Button
          text={t('innerLayout.balanceTransfers.balance.balances.settleNow')}
          disabled={shouldDisableInstantSettlement}
          variant="link"
          size="md"
          leadingIcon={Bolt}
          trailingIcon={ArrowRight}
          onClickHandler={handleSettlementClicked}
        />
      ),
      children: (
        <TableInfo columns={columns} data={upcomingBalance?.items || []} />
      ),
    },
    {
      id: BALANCE_TYPES.AVAILABLE as IBalanceItem['id'],
      label: t('innerLayout.balanceTransfers.balance.balances.available'),
      value: availableBalance?.amount,
      description: availableBalance?.label,
      //   showAction: availableBalance?.can_withdraw,
    },
    {
      id: BALANCE_TYPES.ON_ITS_WAY as IBalanceItem['id'],
      label: t('innerLayout.balanceTransfers.balance.balances.onItsWay'),
      value: onItsWayBalance?.amount,
      description: onItsWayBalance?.label,
    },
  ];

  return (
    <div className={styles['balances']}>
      <InstantSettlement
        isOpen={openModal.settlement}
        handleClose={() => handleCloseModal(BALANCE_MODALS.SETTLEMENT)}
      />
      {BALANCES.map((balance) => {
        return (
          <Fragment key={balance.id}>
            <BalanceItem
              balance={balance}
              currency={currency}
              isLoading={isLoading}
              upcomingBalance={upcomingBalance}
            />
          </Fragment>
        );
      })}
    </div>
  );
};

export default Balances;
